

import React, { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import Card from "@mui/material/Card";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { Typography, CircularProgress } from "@mui/material";
import WordCloud from "react-d3-cloud";
import MetricCardsContainer from "layouts/insight-hub/components/MetricCardsContainer";
import ChartHeader from "examples/Charts/General/ChartHeader"; // ✅ Import new header
import ChartAssistant from "components/ChartAssistant"; // ✅ Import ChartAssistant

import FacebookRoundedIcon from "@mui/icons-material/FacebookRounded";
import YouTubeIcon from "@mui/icons-material/YouTube";
import InstagramIcon from "@mui/icons-material/Instagram";
import TwitterIcon from "@mui/icons-material/Twitter"; // ✅ Added missing import
import ChartNotEntitled from "examples/Charts/General/ChartNotEntitled"; // ✅ Import Entitlement Overlay

// ⚙️ Constants
const MAX_COMMENTS_PER_PROFILE = 500;
const MAX_TOKENS_PER_COMMENT = 100;

// (Worker script is unchanged)
const workerScript = `
    const CLEAN_TEXT_REGEX = /(<[^>]+>)|(https?:\\/\\/\\S+)|(&\\w+;)|[^\\w\\s]/g;
    const stopWords = new Set([
        "the", "and", "you", "for", "are", "but", "with", "this", "that", "have", "not", "was", "your",
        "all", "can", "use", "has", "from", "will", "just", "out", "get", "now", "yes", "yet", "off",
        "more", "its", "our", "who", "how", "too", "very", "try", "won", "far", "app", "air", "sit",
        "non", "fit", "case", "art", "dex", "pre", "ice", "lag", "tft", "own", "isn", "won’t", "did",
        "does", "let", "wasn", "ain", "could", "should", "they", "like", "amp", "tnx", "quot", "when",
        "her", "his", "hasn", "into", "what", "them", "come", "their"
    ]);
    const blacklist = new Set(["href", "http", "https", "www"]);

    const cleanText = (text) => text.replace(CLEAN_TEXT_REGEX, " ");
    const isNumeric = (word) => Number.isInteger(+word);
    const containsBlacklistedWord = (phrase) =>
        phrase.split(" ").some((word) => blacklist.has(word));

    self.onmessage = (event) => {
        const { datasets, ngramSize, maxCommentsPerProfile, maxTokensPerComment } = event.data;
        const results = datasets.map((profile) => {
            const contentField = profile.platform === "YouTube" ? "videos" : "posts";
            const commentField = "comments";
            const allComments = profile.data.flatMap((entry) =>
                (entry[contentField] || []).flatMap((item) => item[commentField] || [])
            );

            const limitedComments = allComments.slice(0, maxCommentsPerProfile);
            const wordFrequency = {};
            limitedComments.forEach((comment) => {
                const raw = comment.text || comment.content || "";
                const cleaned = cleanText(raw.toLowerCase());
                const tokens = cleaned
                    .split(/\\s+/)
                    .slice(0, maxTokensPerComment)
                    .filter(
                        (word) =>
                            word.length > 2 && !stopWords.has(word) && !isNumeric(word) && !blacklist.has(word)
                    );

                for (let i = 0; i <= tokens.length - ngramSize; i++) {
                    const ngram = tokens.slice(i, i + ngramSize).join(" ");
                    if (containsBlacklistedWord(ngram)) continue;
                    wordFrequency[ngram] = (wordFrequency[ngram] || 0) + 1;
                }
            });

            const words = Object.entries(wordFrequency).map(([text, value]) => ({ text, value }));
            return {
                profileName: profile.name || profile.label || "Unknown",
                platform: profile.platform,
                words,
            };
        });

        self.postMessage(results);
    };
`;

function WordCloudChart({
  title,
  datasets,
  previousdatasets = [],
  isLoading: isParentLoading = false,
  ngramSize = 2,
  isEntitled = true,
  featureName = "",
  planRequired = "",
  entitlements
}) {
  const [wordList, setWordList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const worker = useMemo(() => {
    // ... (worker memo unchanged)
  }, []);

  useEffect(() => {
    if (!datasets || datasets.length === 0) {
      setIsLoading(false);
      setWordList([]);
      return;
    }

    setIsLoading(true);

    const blob = new Blob([workerScript], { type: "application/javascript" });
    const blobUrl = URL.createObjectURL(blob);
    const workerInstance = new Worker(blobUrl);

    workerInstance.onmessage = (event) => {
      setWordList(event.data);
      setIsLoading(false);
    };

    workerInstance.onerror = (error) => {
      console.error("Worker error:", error);
      setIsLoading(false);
    };

    workerInstance.postMessage({
      datasets,
      ngramSize,
      maxCommentsPerProfile: MAX_COMMENTS_PER_PROFILE,
      maxTokensPerComment: MAX_TOKENS_PER_COMMENT,
    });

    // Cleanup
    return () => {
      workerInstance.terminate();
      URL.revokeObjectURL(blobUrl);
    };
  }, [datasets, ngramSize]);


  const commentMetrics = useMemo(() => {
    if (!datasets) return []; // ✅ Added guard
    return datasets.map((profile) => {
      if (!profile) return null; // ✅ Added guard
      const contentField = profile.platform === "YouTube" ? "videos" : "posts";
      const commentField = "comments";

      const currentCount = (profile.data || []).reduce( // ✅ Added guard
        (acc, entry) =>
          acc + (entry[contentField]?.reduce((sum, item) => sum + (item[commentField]?.length || 0), 0) || 0),
        0
      );

      const previousProfile = previousdatasets?.find((p) => p.profileId === profile.profileId);
      const previousCount = previousProfile
        ? (previousProfile.data || []).reduce( // ✅ Added guard
          (acc, entry) =>
            acc + (entry[contentField]?.reduce((sum, item) => sum + (item[commentField]?.length || 0), 0) || 0),
          0
        )
        : 0;

      return {
        profilePic: profile.profilePictureUrl || "https://via.placeholder.com/150",
        name: profile.name || profile.label || "Unknown",
        platform: profile.platform,
        metricName: "Comment Count",
        metricValue: currentCount.toLocaleString(),
        previousMetricValue: previousCount.toLocaleString(),
      };
    }).filter(Boolean); // ✅ Filter out nulls
  }, [datasets, previousdatasets]);

  if ((!datasets || datasets.length === 0) && !isParentLoading) {
    return (
      <Card>
        <MDBox p={3} textAlign="center">
          <MDTypography variant="body2" color="text">
            No data available for Word Cloud.
          </MDTypography>
        </MDBox>
      </Card>
    );
  }

  const getPlatformIcon = (platform) => {
    switch (platform?.toLowerCase()) {
      case "facebook":
        return <FacebookRoundedIcon sx={{ fontSize: 18, color: "#282828", mr: 0.5 }} />;
      case "youtube":
        return <YouTubeIcon sx={{ fontSize: 18, color: "#282828", mr: 0.5 }} />;
      case "instagram":
        return <InstagramIcon sx={{ fontSize: 18, color: "#282828", mr: 0.5 }} />;
      case "twitter":
        return <TwitterIcon sx={{ fontSize: 18, color: "#282828", mr: 0.5 }} />;
      default:
        return null;
    }
  };

  // ⬇️=========================================
  // ⬇️ ADDED: Tooltip content
  // ⬇️=========================================
  const tooltipContent = (
    <div style={{ maxWidth: "350px", textAlign: "left" }}>
      <Typography variant="body2" gutterBottom>
        <strong>Description:</strong>
      </Typography>
      <Typography variant="caption" display="block" gutterBottom>
        This chart analyzes all comments from the selected date range and displays the most frequently used words or phrases (n-grams).
      </Typography>

      <Typography variant="body2" gutterBottom>
        <strong>How to read the chart:</strong>
      </Typography>
      <Typography variant="caption" display="block" gutterBottom>
        - The larger the word, the more frequently it appeared in comments. <br />
        - The chart is generated by processing a sample of recent comments.
      </Typography>

      <Typography variant="body2" gutterBottom>
        <strong>Insights you can gain:</strong>
      </Typography>
      <Typography variant="caption" display="block">
        - Quickly identify key topics, popular terms, or emerging sentiment in your audience's feedback. <br />
        - Understand what users are talking about most in response to your content.
      </Typography>
    </div>
  );

  // ⬇️=========================================
  // ⬇️ ADDED: Text Context for AI (Fix for [object Object] and missing data)
  // ⬇️=========================================
  const aiContext = useMemo(() => {
    let text = `Chart Description: This chart analyzes all comments and displays the most frequently used words or phrases (n-grams). The larger the word, the more frequently it appeared.
     
     Insights contained in this chart:`;

    if (wordList && wordList.length > 0) {
      wordList.forEach(profile => {
        text += `\n\nProfile: ${profile.profileName} (${profile.platform})`;
        const topWords = profile.words
          .sort((a, b) => b.value - a.value)
          .slice(0, 100) // Top 100 words
          .map(w => `${w.text} (${w.value})`)
          .join(", ");
        text += `\nTop Words: ${topWords}`;
      });
    } else {
      text += "\nNo word data available.";
    }
    return text;
  }, [wordList]);

  // ⬇️=========================================
  // ⬇️ ADDED: Synthetic Chart Data for AI (Bar Chart Equivalent)
  // ⬇️=========================================
  const aiChartData = useMemo(() => {
    if (!wordList || wordList.length === 0) return null;

    // 1. Collect all unique words from top 30 of each profile to form X-axis labels
    const allWords = new Set();
    wordList.forEach(p => {
      // Take top 30 words per profile for the "visual" chart representation
      p.words.sort((a, b) => b.value - a.value).slice(0, 30).forEach(w => allWords.add(w.text));
    });
    const labels = Array.from(allWords);

    // 2. Build datasets mapping counts to these labels
    const aiDatasets = wordList.map(profile => ({
      label: `${profile.profileName} (${profile.platform})`,
      data: labels.map(label => {
        const match = profile.words.find(w => w.text === label);
        return match ? match.value : 0;
      })
    }));

    return {
      labels,
      datasets: aiDatasets
    };
  }, [wordList]);
  // ⬆️=========================================


  return (
    <Card sx={{ display: "flex", flexDirection: "column" }}>
      {/* ⬇️ REPLACED HEADER ⬇️ */}
      <ChartHeader
        title={title}
        description={tooltipContent}
        profilePictures={commentMetrics.map(m => m.profilePic)}
        is_derived={true} // This is a derived metric
      >
        {/* ✅ AI Assistant */}
        <ChartAssistant
          title={title}
          context={aiContext} // ✅ Pass TEXT string
          chartData={aiChartData} // ✅ Pass Semantic Bar Chart Data
          rawData={wordList} // ✅ Pass Raw Word List
          sx={{
            position: "static",
            boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
            ml: 2
          }}
          entitlements={entitlements} // ✅ Pass entitlements
        />
      </ChartHeader>
      {/* ⬆️ REPLACED HEADER ⬆️ */}

      {/* ⬆️ REPLACED HEADER ⬆️ */}

      {/* WRAPPER FOR CONTENT BELOW HEADER (For Overlay) */}
      <div style={{ position: "relative", flexGrow: 1, display: "flex", flexDirection: "column" }}>

        {/* 🚫 Frosted glass entitlement overlay */}
        {!isEntitled && (
          <ChartNotEntitled
            featureName={featureName}
            planRequired={planRequired}
          />
        )}

        {/* Disclaimer Footer - Added at top level relative container to match others if needed, or keeping inside Card */}
        {/* We place it at the bottom of the flex container */}

        {/* Metrics */}
        <MetricCardsContainer metrics={commentMetrics} />
        <MDBox mt={1} />

        {/* Scrollable Word Cloud Section */}
        <MDBox
          p={3}
          sx={{
            flexGrow: 1,
            overflowY: "auto",
            minHeight: 0,
          }}
        >
          <MDBox display="flex" flexWrap="wrap" justifyContent="center" gap={4}>
            {isLoading || isParentLoading ? (
              <MDBox display="flex" justifyContent="center" alignItems="center" sx={{ minHeight: "250px" }}>
                <CircularProgress size={32} thickness={4} color="accent" />
              </MDBox>
            ) : (
              wordList.map(({ profileName, platform, words }, idx) => (
                <React.Fragment key={idx}>
                  {words.length > 0 ? (
                    <MDBox
                      textAlign="center"
                      width={{ xs: "100%", md: "45%", lg: "30%" }}
                    >
                      <MDTypography
                        variant="subtitle2"
                        color="text"
                        mb={1}
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        gap={0.5}
                      >
                        {getPlatformIcon(platform)}{profileName}
                      </MDTypography>
                      <WordCloud
                        data={words}
                        font="Arial"
                        fontSize={(word) => Math.log2(word.value + 1) * 10}
                        spiral="archimedean"
                        padding={2}
                        rotate={() => 0}
                        width={300}
                        height={250}
                      />

                    </MDBox>
                  ) : (
                    <MDBox textAlign="center">
                      <MDTypography
                        variant="subtitle2"
                        color="text"
                        mb={1}
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        gap={0.5}
                      >
                        {getPlatformIcon(platform)}{profileName}
                      </MDTypography>

                      <MDTypography variant="subtitle1" color="text" align="center">
                        No comments found in the selected date range.
                      </MDTypography>
                    </MDBox>
                  )}
                </React.Fragment>
              ))
            )}
          </MDBox>
        </MDBox>
        {/* Disclaimer Footer */}
        <MDBox p={1} pb={2} display="flex" justifyContent="flex-end">
          <MDTypography
            variant="caption"
            color="text"
            sx={{
              fontStyle: "italic",
              fontSize: "0.7rem",
              opacity: 0.7,
              mr: 1
            }}
          >
            *Metric calculated by Comparedly
          </MDTypography>
        </MDBox>

      </div>
    </Card>
  );

}

WordCloudChart.propTypes = {
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  datasets: PropTypes.array.isRequired,
  previousdatasets: PropTypes.array,
  isLoading: PropTypes.bool,
  ngramSize: PropTypes.number,
  isEntitled: PropTypes.bool,
  featureName: PropTypes.string,
  planRequired: PropTypes.string,
  entitlements: PropTypes.object,
};

export default WordCloudChart;